import { useEffect, useState, useMemo } from "react";
import { api, resolveFileUrl } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Users, Search, Mail, Phone, KeyRound, GraduationCap, FileSpreadsheet,
  UserPlus, Edit, Eye, Trash2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { SchoolSelect, useSchools } from "../components/SchoolSelect";
import { localName, localFullName } from "../lib/localName";

export default function ParentsPage() {
  const {  t , lang } = useI18n();
  const nav = useNavigate();
  const { schools, schoolId, setSchoolId, isSuper, queryId } = useSchools({ allowAll: true });
  const [parents, setParents] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = () => {
    setLoading(true);
    const qs = queryId ? `?school_id=${queryId}` : "";
    api.get(`/parents${qs}`)
      .then(r => setParents(r.data || []))
      .catch(() => setParents([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [queryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!q) return parents;
    const needle = q.toLowerCase();
    return parents.filter(p =>
      (p.email || "").toLowerCase().includes(needle) ||
      (p.name || "").toLowerCase().includes(needle) ||
      (p.phone || "").includes(q) ||
      (p.children || []).some(c => (localFullName(c, lang) || "").includes(q))
    );
  }, [parents, q]);

  const totalChildren = parents.reduce((sum, p) => sum + (p.children_count || 0), 0);

  const openEdit = (p) => {
    setSelected(p);
    setEditForm({ name: p.name || "", phone: p.phone || "", email: p.email || "", active: p.active !== false });
  };

  const saveEdit = async () => {
    if (!editForm.email || !editForm.email.includes("@")) { toast.error(t("pp_email_invalid")); return; }
    try {
      await api.put(`/parents/${selected.id}`, editForm);
      toast.success(t("pp_saved"));
      setSelected(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openResetPw = (p) => {
    setSelected(p);
    setNewPw(generatePw());
    setPwOpen(true);
  };

  const resetPw = async () => {
    if (!newPw || newPw.length < 6) { toast.error(t("pp_pw_too_short")); return; }
    try {
      await api.put(`/parents/${selected.id}`, { password: newPw });
      toast.success(t("pp_pw_reset_done"));
      // Keep dialog open so admin can copy the new password
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const { data } = await api.delete(`/parents/${deleteConfirm.id}`);
      toast.success(`${t("pp_parent_deleted")}${data.unlinked_students ? ` · ${t("pp_unlinked_students")} ${data.unlinked_students} ${t("pp_student_word")}` : ""}`);
      setDeleteConfirm(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="relative bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-6 sm:p-8">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 80% 0%, rgba(255,255,255,.4), transparent 40%)" }} />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
                <Users className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">{t("parents_title")}</h1>
                <p className="text-sm text-primary-foreground/80 mt-1">
                  {t("parents_subtitle")}
                </p>
              </div>
            </div>
            <Button onClick={() => nav("/import/students")} className="bg-white/20 hover:bg-white/30 backdrop-blur border border-white/30" data-testid="parents-import-btn">
              <FileSpreadsheet className="w-4 h-4 me-2" /> {t("import_from_excel")}
            </Button>
          </div>
        </div>
      </Card>

      {/* School scope selector */}
      {(isSuper || schools.length > 0) && (
        <Card className="p-4 border shadow-none flex flex-wrap items-center gap-3">
          <Users className="w-4 h-4 text-primary shrink-0" />
          <span className="text-sm font-medium text-muted-foreground">{t("select_school")}</span>
          <SchoolSelect
            schools={schools}
            value={schoolId}
            onChange={setSchoolId}
            testId="parents-school-select"
            disabled={!isSuper}
            showAll={isSuper}
          />
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="p-4 border shadow-none">
          <Users className="w-5 h-5 text-primary mb-2" />
          <div className="text-2xl font-bold">{parents.length}</div>
          <div className="text-xs text-muted-foreground">{t("parents")}</div>
        </Card>
        <Card className="p-4 border shadow-none">
          <GraduationCap className="w-5 h-5 text-secondary mb-2" />
          <div className="text-2xl font-bold">{totalChildren}</div>
          <div className="text-xs text-muted-foreground">{t("parent_children_count")}</div>
        </Card>
        <Card className="p-4 border shadow-none">
          <Mail className="w-5 h-5 text-accent mb-2" />
          <div className="text-2xl font-bold">{parents.filter(p => p.children_count === 0).length}</div>
          <div className="text-xs text-muted-foreground">{t("parent_no_account")}</div>
        </Card>
      </div>

      {/* Search */}
      <Card className="p-4 border shadow-none">
        <div className="relative">
          <Search className="absolute top-3 start-3 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("pp_search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="ps-10"
            data-testid="parents-search"
          />
        </div>
      </Card>

      {/* List */}
      {loading ? (
        <Card className="p-12 text-center text-muted-foreground">{t("pp_loading")}</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-dashed">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
          {q ? t("pp_no_results") : t("pp_no_parents_yet")}
        </Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {filtered.map(p => (
            <Card
              key={p.id}
              className="p-4 sm:p-5 border border-border/60 rounded-2xl shadow-sm touch-card cursor-pointer hover:border-primary/40 transition"
              data-testid={`parent-card-${p.id}`}
              onClick={() => nav(`/parents/${p.id}`)}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center font-bold text-lg shrink-0 overflow-hidden">
                  {p.photo_url
                    ? <img src={resolveFileUrl(p.photo_url)} alt="" className="w-full h-full object-cover" />
                    : (p.name || p.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{p.name || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono truncate flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {p.email}
                  </div>
                  {p.phone && (
                    <div className="text-xs text-muted-foreground font-mono mt-0.5 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {p.phone}
                    </div>
                  )}
                </div>
                <div className="text-end">
                  <div className="text-2xl font-bold text-primary">{p.children_count || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{t("pp_children")}</div>
                </div>
              </div>

              {p.children?.length > 0 && (
                <div className="border-t border-border pt-2 mb-3">
                  <div className="text-xs text-muted-foreground mb-1">{t("pp_linked_children")}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.children.slice(0, 5).map(c => (
                      <span key={c.id} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
                        {localFullName(c, lang)}
                      </span>
                    ))}
                    {p.children.length > 5 && (
                      <span className="text-xs text-muted-foreground">+{p.children.length - 5}</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t border-border" onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1" data-testid={`edit-${p.id}`}>
                  <Edit className="w-3.5 h-3.5 me-1" /> {t("pp_edit_btn")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => openResetPw(p)} className="flex-1" data-testid={`reset-pw-${p.id}`}>
                  <KeyRound className="w-3.5 h-3.5 me-1" /> {t("pp_password_btn")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(p)} className="text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid={`delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!selected && !pwOpen} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pp_edit_dialog_title")}</DialogTitle>
            <DialogDescription>{t("pp_edit_dialog_desc")}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div>
                <Label>{t("sip_email")} <span className="text-destructive">*</span></Label>
                <Input
                  type="email"
                  value={editForm.email || ""}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="font-mono"
                  data-testid="parent-edit-email"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground mt-1">{t("pp_email_login_note")}</p>
              </div>
              <div>
                <Label>{t("sip_name")}</Label>
                <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="parent-edit-name" />
              </div>
              <div>
                <Label>{t("pp_phone")}</Label>
                <Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="+9665..." data-testid="parent-edit-phone" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.active !== false}
                  onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                  data-testid="parent-edit-active"
                />
                <span className="text-sm">{t("pp_account_active")}</span>
              </label>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setSelected(null)} className="flex-1">{t("cancel")}</Button>
                <Button onClick={saveEdit} className="flex-1" data-testid="parent-edit-save">{t("save")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={pwOpen} onOpenChange={(v) => { setPwOpen(v); if (!v) { setSelected(null); setNewPw(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pp_pw_dialog_title")}</DialogTitle>
            <DialogDescription>{t("pp_pw_dialog_desc")}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="text-muted-foreground">{t("pp_user_label")}</span> <span className="font-semibold">{selected.name}</span>
                <div className="text-xs text-muted-foreground font-mono">{selected.email}</div>
              </div>
              <div>
                <Label>{t("pp_new_password")}</Label>
                <div className="flex gap-2">
                  <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} className="font-mono" data-testid="parent-new-pw" />
                  <Button variant="outline" onClick={() => setNewPw(generatePw())}>{t("pp_generate")}</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t("pp_pw_save_warning")}</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => { setPwOpen(false); setSelected(null); }} className="flex-1">{t("pp_close")}</Button>
                <Button onClick={resetPw} className="flex-1" data-testid="parent-confirm-reset">
                  <KeyRound className="w-4 h-4 me-2" /> {t("pp_apply")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> {t("pp_confirm_delete_title")}
            </DialogTitle>
            <DialogDescription>{t("pp_no_undo")}</DialogDescription>
          </DialogHeader>
          {deleteConfirm && (
            <div className="space-y-4">
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-sm">
                <div className="font-semibold mb-2">{t("pp_will_delete")}</div>
                <div className="font-mono text-xs">{deleteConfirm.email}</div>
                <div className="mt-1">{deleteConfirm.name}</div>
                {deleteConfirm.children_count > 0 && (
                  <div className="mt-3 pt-3 border-t border-destructive/30">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-xs">
                        <b>{t("pp_linked_warning_a")}</b> <b>{deleteConfirm.children_count}</b> {t("pp_linked_warning_b")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1">{t("cancel")}</Button>
                <Button variant="destructive" onClick={confirmDelete} className="flex-1" data-testid="parent-confirm-delete">
                  <Trash2 className="w-4 h-4 me-2" /> {t("pp_delete_permanent")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function generatePw() {
  const chars = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}
